import { zodResolver } from '@hookform/resolvers/zod';
import {
  type Account,
  exchangeDealRate,
  type ExchangeRequest,
  sortCurrencies,
  type TransactionDefaults,
} from '@familywallet/shared';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { type FieldError, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { FormError } from '../components/FormError';
import { Chip, Row, SwapRow } from '../components/FormRow';
import { MoneyField } from '../components/MoneyField';
import { PickerSheet, type PickerOption } from '../components/PickerSheet';
import { Button } from '../components/ui/button';
import { ListGroup } from '../components/ui/card';
import { parsePositive } from '../lib/add-form';
import { api } from '../lib/api';
import { todayIn } from '../lib/dates';
import { type ExchangeFormValues, exchangeFormSchema } from '../lib/fx-forms';
import { useInvalidateLedger } from '../lib/ledger-mutations';
import { useIdempotencyKey } from '../lib/members';
import { formatAmount, formatRateValue, groupAmountInput } from '../lib/money';

const currenciesOf = (account: Account | undefined, baseCurrency: string) =>
  sortCurrencies(account?.balances.map((b) => b.currency) ?? [], baseCurrency);

/**
 * Exchange: one currency leaves an account, another arrives in the same account or another one
 * (through the FX trading accounts). Shows the rate the deal works out to next to the family's.
 */
export function ExchangeForm({
  accounts,
  defaults,
  baseCurrency,
  timezone,
  latestRates,
  myId,
}: {
  accounts: Account[];
  defaults: TransactionDefaults;
  baseCurrency: string;
  timezone: string;
  latestRates: Record<string, string>;
  myId: string;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const invalidate = useInvalidateLedger();
  const [idempotencyKey, renewKey] = useIdempotencyKey();
  const today = todayIn(timezone);
  const byId = useMemo(() => new Map(accounts.map((a) => [a.id, a])), [accounts]);
  const [picker, setPicker] = useState<'fromAccountId' | 'toAccountId' | null>(null);

  // Start from an account with two currencies when there is one (the usual wallet exchange).
  const mine = accounts.filter((a) => a.ownerMemberId === myId);
  const start =
    [...mine, ...accounts].find((a) => a.balances.length > 1) ??
    byId.get(defaults.lastAccountId.transfer ?? '') ??
    mine[0] ??
    accounts[0]!;
  const startCurrencies = currenciesOf(start, baseCurrency);
  const startTo =
    startCurrencies.length > 1
      ? start
      : (accounts.find((a) =>
          currenciesOf(a, baseCurrency).some((c) => c !== startCurrencies[0]),
        ) ?? start);
  const startToCurrency =
    currenciesOf(startTo, baseCurrency).find((c) => c !== startCurrencies[0]) ?? '';

  const form = useForm<ExchangeFormValues, unknown, ExchangeRequest>({
    resolver: zodResolver(exchangeFormSchema),
    defaultValues: {
      fromAccountId: start.id,
      fromCurrency: startCurrencies[0] ?? '',
      fromAmount: '',
      toAccountId: startTo.id,
      toCurrency: startToCurrency,
      toAmount: '',
      occurredOn: today,
      description: '',
    },
  });
  const v = useWatch({ control: form.control }) as ExchangeFormValues;
  const fromOptions = currenciesOf(byId.get(v.fromAccountId), baseCurrency);
  const toOptions = currenciesOf(byId.get(v.toAccountId), baseCurrency).filter(
    (c) => c !== v.fromCurrency,
  );

  // Keep both currencies ones the chosen accounts hold, and different from each other.
  useEffect(() => {
    if (!fromOptions.includes(v.fromCurrency) && fromOptions[0]) {
      form.setValue('fromCurrency', fromOptions[0]);
    }
    if (!toOptions.includes(v.toCurrency)) form.setValue('toCurrency', toOptions[0] ?? '');
  }, [fromOptions, toOptions, v.fromCurrency, v.toCurrency, form]);

  const save = useMutation({
    mutationFn: (body: ExchangeRequest) => api.transactions.exchange(body, { idempotencyKey }),
    onSuccess: async () => {
      renewKey();
      await invalidate();
      navigate('/', { state: { saved: true } });
    },
  });

  const fromAmount = parsePositive(v.fromAmount, v.fromCurrency);
  const toAmount = parsePositive(v.toAmount, v.toCurrency);
  const deal =
    fromAmount && toAmount && v.toCurrency
      ? exchangeDealRate(
          { amount: fromAmount, currency: v.fromCurrency },
          { amount: toAmount, currency: v.toCurrency },
          baseCurrency,
        )
      : null;
  const familyRate = deal ? latestRates[deal.currency] : undefined;
  const errors = form.formState.errors;
  const errorText = (error: FieldError | undefined, currency = '') =>
    error?.message ? t(error.message, { currency }) : null;
  const reformat = (name: 'fromAmount' | 'toAmount', currency: string) => {
    const parsed = parsePositive(form.getValues(name), currency);
    if (parsed) form.setValue(name, groupAmountInput(parsed, currency));
  };

  // ⇅ turns the deal around: accounts, currencies and amounts change sides.
  const swap = () => {
    const x = form.getValues();
    form.setValue('fromAccountId', x.toAccountId);
    form.setValue('toAccountId', x.fromAccountId);
    form.setValue('fromCurrency', x.toCurrency);
    form.setValue('toCurrency', x.fromCurrency);
    form.setValue('fromAmount', x.toAmount);
    form.setValue('toAmount', x.fromAmount);
  };

  const accountOptions: PickerOption[] = accounts.map((a) => ({
    value: a.id,
    label: a.name,
    hint: a.balances.map((b) => formatAmount(b.balance, b.currency, i18n.language)).join(' · '),
  }));

  return (
    <form
      noValidate
      className="flex flex-col gap-4"
      onSubmit={form.handleSubmit((body) => save.mutate(body))}
    >
      <MoneyField
        id="exchange-from"
        label={t('exchange.give')}
        register={form.register('fromAmount', {
          onBlur: () => reformat('fromAmount', v.fromCurrency),
        })}
        currency={v.fromCurrency}
        currencies={fromOptions}
        onCurrency={(c) => form.setValue('fromCurrency', c)}
        error={errorText(errors.fromAmount, v.fromCurrency)}
        preview={{ raw: v.fromAmount, baseCurrency, rate: latestRates[v.fromCurrency] }}
      />
      <MoneyField
        id="exchange-to"
        label={t('exchange.get')}
        register={form.register('toAmount', {
          onBlur: () => reformat('toAmount', v.toCurrency),
        })}
        currency={v.toCurrency}
        currencies={toOptions}
        onCurrency={(c) => form.setValue('toCurrency', c)}
        error={errorText(errors.toAmount, v.toCurrency) ?? errorText(errors.toCurrency)}
        preview={{ raw: v.toAmount, baseCurrency, rate: latestRates[v.toCurrency] }}
      />
      {toOptions.length === 0 && (
        <p role="alert" className="rounded-xl border border-line bg-surface p-3 text-sm">
          {t('exchange.noOtherCurrency')}
        </p>
      )}
      {deal && (
        <p className="px-1 text-sm text-muted" aria-live="polite" dir="ltr">
          {t('exchange.dealRate', {
            base: baseCurrency,
            rate: formatRateValue(deal.rate, i18n.language),
            quote: deal.currency,
          })}
          {familyRate &&
            ` · ${t('exchange.familyRate', { rate: formatRateValue(familyRate, i18n.language) })}`}
        </p>
      )}

      <ListGroup>
        <Row label={t('exchange.fromAccount')} error={errorText(errors.fromAccountId)}>
          <Chip onClick={() => setPicker('fromAccountId')}>
            {byId.get(v.fromAccountId)?.name ?? t('add.pickAccount')}
          </Chip>
        </Row>
        <SwapRow label={t('add.swap')} onSwap={swap} />
        <Row label={t('exchange.toAccount')} error={errorText(errors.toAccountId)}>
          <Chip onClick={() => setPicker('toAccountId')}>
            {byId.get(v.toAccountId)?.name ?? t('add.pickAccount')}
          </Chip>
        </Row>
        <Row label={t('add.date')} htmlFor="exchange-date" error={errorText(errors.occurredOn)}>
          <input
            id="exchange-date"
            type="date"
            max={today}
            className="min-h-11 rounded-full bg-surface-2 px-3 text-sm font-medium text-fg focus-visible:outline-2 focus-visible:outline-accent"
            {...form.register('occurredOn')}
          />
        </Row>
        <Row label={t('add.note')} htmlFor="exchange-note">
          <input
            id="exchange-note"
            autoComplete="off"
            maxLength={200}
            placeholder={t('add.notePlaceholder')}
            className="min-h-11 w-full min-w-0 bg-transparent text-end text-[15px] placeholder:text-muted focus-visible:outline-none"
            {...form.register('description')}
          />
        </Row>
      </ListGroup>
      <p className="px-1 text-xs text-muted">{t('exchange.hint')}</p>

      <FormError error={save.error} />
      <Button type="submit" disabled={save.isPending || toOptions.length === 0}>
        {save.isPending ? t('common.saving') : t('add.save')}
      </Button>

      {picker && (
        <PickerSheet
          title={t('add.pickAccount')}
          options={accountOptions}
          selected={v[picker]}
          onPick={(id) => {
            form.setValue(picker, id);
            setPicker(null);
          }}
          onClose={() => setPicker(null)}
        />
      )}
    </form>
  );
}
